import React from "react";
import { useLocationQuery } from "../../hooks";
import Flex from "../Flex/Flex";
import { Facet } from "./Facet";
import { IActiveFacet, IFacetsProps } from "./types";

function getActiveQueryValue<TFacetName extends string>(queryValues: TFacetName[]): TFacetName | null | undefined {
    if (queryValues.length === 0) {
        return null;
    }

    if (queryValues.length === 1) {
        return queryValues[0];
    }

    return undefined;
}

const getTotalCount = <TFacetName extends string, TQueryName extends string = string>(
    values: IFacetsProps<TFacetName, TQueryName>["values"],
    names: IFacetsProps<TFacetName, TQueryName>["names"]
): number => {
    if (!values) return 0;

    return names.reduce((acc, name) => {
        const value: number | string | undefined = values[name];

        if (typeof value === "string") {
            return acc;
        }

        return acc + (value ?? 0);
    }, 0);
};

const Facets = <TFacetName extends string, TQueryName extends string = string>({
    names,
    disabledNames = [],
    values,
    query,
    all = false,
    allLabel = "All",
    getLabel = facetName => facetName,
    getBadgeProps,
    ignoreFilters = ["page"],
    keepFiltersOnSelect = false,
    defaultSelection,
    onChange,
    ...props
}: IFacetsProps<TFacetName, TQueryName>) => {
    const { getAll, set, del, clear, activeFilters } = useLocationQuery<TQueryName>();
    const totalCount = all ? getTotalCount(values, names) : 0;
    const restFilters = query && activeFilters.filter(filter => ![...ignoreFilters, query].includes(filter));

    const queryValue = query && !restFilters?.length ? getActiveQueryValue(getAll<TFacetName>(query) ?? []) : undefined;
    const [activeFacet, setActiveFacet] = React.useState<IActiveFacet<TFacetName> | undefined>(() => {
        if (all && queryValue === null) {
            return { name: "all", value: totalCount };
        }
        const name = names.find(
            facetName =>
                queryValue === facetName || (!all && !queryValue && defaultSelection && facetName === defaultSelection)
        );
        if (!name) return undefined;

        return { name, value: values?.[name] ?? 0 };
    });

    React.useEffect(() => {
        onChange?.(activeFacet);
    }, [activeFacet]);

    return (
        <Flex color="text-primary-700" spacing={1} {...props}>
            {all && (
                <Facet
                    active={activeFacet?.name === "all"}
                    onClick={() => {
                        if (query) {
                            !keepFiltersOnSelect && clear();
                            del(query);
                            setActiveFacet({ name: "all", value: totalCount });
                        }
                    }}
                    label={allLabel}
                    value={totalCount}
                    badgeProps={{ variant: "default" }}
                />
            )}
            {names.map(facetName => (
                <Facet
                    key={facetName}
                    active={activeFacet?.name === facetName}
                    onClick={() => {
                        if (query) {
                            !keepFiltersOnSelect && clear();
                            defaultSelection === facetName ? del(query) : set(query, facetName);
                            setActiveFacet({ name: facetName, value: values?.[facetName] });
                        }
                    }}
                    label={getLabel(facetName)}
                    value={values?.[facetName]}
                    badgeProps={getBadgeProps?.(facetName)}
                    disabled={disabledNames.includes(facetName)}
                />
            ))}
        </Flex>
    );
};

export default Facets;
